/**
 * Decodes JWT token payload to extract claims
 * @param token - JWT token string
 * @returns Decoded payload object or null if invalid
 */
function decodeJwtPayload(token: string): Record<string, any> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];
    const paddedPayload = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    const decodedPayload = JSON.parse(atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/')));

    return decodedPayload;
  } catch (error) {
    console.error('Error decoding JWT payload:', error);
    return null;
  }
}

/**
 * Gets the expiration time (exp claim) from a JWT token
 * @param token - JWT token string
 * @returns Expiration timestamp in seconds, or null if invalid or missing
 */
export function getTokenExpiration(token: string): number | null {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') {
    return null;
  }
  return payload.exp;
}

/**
 * Checks if a JWT token is expired
 * @param token - JWT token string
 * @returns True if token is expired, false otherwise, null if token is invalid
 */
export function isTokenExpired(token: string): boolean | null {
  const exp = getTokenExpiration(token);
  if (exp === null) {
    return null;
  }

  const now = Math.floor(Date.now() / 1000);
  return exp <= now;
}

/**
 * Calculates milliseconds until token expires
 * @param token - JWT token string
 * @returns Milliseconds until expiration, or null if invalid or already expired
 */
export function getMsUntilExpiration(token: string): number | null {
  const exp = getTokenExpiration(token);
  if (exp === null) {
    return null;
  }

  const now = Math.floor(Date.now() / 1000);
  const secondsUntilExpiration = exp - now;

  if (secondsUntilExpiration <= 0) {
    return null;
  }

  return secondsUntilExpiration * 1000;
}
